// Performance (DESIGN §3): "How is production performing?"
// Route: #/performance?range=7d|30d|3m|6m|1y&client=<id>&month=<1-24> (defaults: 30d, everything).
// One TEAM PERFORMANCE score card (score + delta + lime line with a gradient, range + Client/Month scope), four compact
// KPI cards, then the TEAM list — alphabetical, never ranked, each row a link to /people/:id.
// Reusable exports: ScoreCard, RangeButtons, KpiCards, formatScore/formatPct/formatDays/formatPoints, scopeFromQuery.
import './performance.css';
import { useMemo } from 'preact/hooks';
import { useStore } from '../store.js';
import { buildPath, href, setQuery } from '../router.js';
import { Page } from '../components/page.jsx';
import { Panel } from '../components/panel.jsx';
import { Delta, MetricCard } from '../components/metric-card.jsx';
import { FilterButtons } from '../components/filter-buttons.jsx';
import { CompactDropdown } from '../components/compact-dropdown.jsx';
import { Popover, usePopover } from '../components/popover.jsx';
import { AreaChart } from '../components/chart.jsx';
import { Avatar, EmptyState, cx } from '../components/ui.jsx';
import { Icon } from '../icons.jsx';
import { computePerformance, normalizeRange } from '../../shared/performance.js';
import { activeNow, monthCountOf, peopleStats } from '../../shared/derive.js';
import { DEFAULT_RANGE, MAX_MONTH, PERFORMANCE_RANGES, monthLabel, plural } from '../../shared/constants.js';
import { formatDateRange } from '../../shared/dates.js';

// ---------- formatting ----------

const isNum = (v) => typeof v === 'number' && Number.isFinite(v);
const one = (v) => (Math.round(v * 10) / 10).toFixed(1);

export const formatScore = (v) => (isNum(v) ? one(v) : '—');
export const formatPct = (v) => (isNum(v) ? `${Math.round(v)}%` : '—');
export const formatDays = (v) => (isNum(v) ? `${one(v)}d` : '—');
// Deltas: percentage points / score points / percent.
export const formatPoints = (d, unit = 'pts') => `${d > 0 ? '+' : ''}${one(d)} ${unit}`;
const formatPp = (d) => formatPoints(d, 'pp');
const formatPercent = (d) => `${d > 0 ? '+' : ''}${Math.round(d)}%`;

export const rangeOf = (key) => PERFORMANCE_RANGES.find((r) => r.key === key) || PERFORMANCE_RANGES[1];
const priorText = (key) => `vs prior ${rangeOf(key).long.replace(/^Last /, '')}`;

// Query → scope. Unknown clients and out-of-range months fall back to "all".
export function scopeFromQuery(query = {}, index) {
  const range = normalizeRange(query.range || DEFAULT_RANGE);
  const clientId = query.client && index?.clientsById.has(query.client) ? query.client : '';
  const n = Number(query.month);
  const monthNumber = Number.isInteger(n) && n >= 1 && n <= MAX_MONTH ? n : 0;
  return { range, clientId, monthNumber };
}

// ---------- pieces ----------

export function RangeButtons({ value, onChange }) {
  return (
    <FilterButtons
      class="perf-range"
      size="sm"
      variant="text"
      label="Time range"
      value={value}
      onChange={onChange}
      options={PERFORMANCE_RANGES.map((r) => ({ value: r.key, label: r.label }))}
    />
  );
}

// "?" next to the score: explains how it is computed (click/Enter; Esc or outside click closes).
function ScoreInfo() {
  const pop = usePopover();
  return (
    <>
      <button type="button" class="perf-info" ref={pop.anchorRef} aria-label="How the score works" {...pop.triggerProps}>?</button>
      <Popover open={pop.open} onClose={pop.close} anchorRef={pop.anchorRef} placement="bottom-start" width={280} label="How the score works">
        <div class="perf-info-body">
          <strong>Score (0–100)</strong>
          <p>The average of the on-time rate (finished batches delivered by their due date) and the completion rate (batches due in the period that are finished).</p>
          <p>Each point on the line covers the trailing weeks, so it moves smoothly.</p>
        </div>
      </Popover>
    </>
  );
}

// Y scale that shows movement: 10-point steps around the data, within 0–100, at least 20 points tall.
function scoreDomain(values) {
  const xs = values.filter(isNum);
  if (!xs.length) return [0, 100];
  let lo = Math.max(0, Math.floor((Math.min(...xs) - 5) / 10) * 10);
  const hi = Math.min(100, Math.ceil((Math.max(...xs) + 5) / 10) * 10);
  if (hi - lo < 20) lo = Math.max(0, hi - 20);
  return [lo, hi];
}
const axisScore = (v) => (isNum(v) ? (Number.isInteger(v) ? String(v) : one(v)) : '—');

// A trailing-window score with nothing in its window yet keeps its last value up to today, so the line reaches the
// right edge instead of stopping in mid-air with a wall under it.
function carryToEnd(points) {
  let last = -1;
  points.forEach((p, i) => { if (isNum(p.value)) last = i; });
  if (last < 0) return points;
  return points.map((p, i) => (i > last ? { ...p, value: points[last].value } : p));
}

// The big card: score (0–100, 1 decimal) + delta + lime line with a subtle gradient. `perf` = computePerformance(...).
// actions: range buttons and optional scope dropdowns (rendered in the header, right side).
export function ScoreCard({ title = 'Team Performance', perf, range, actions, emptyText = 'No batches finished or due in this period.' }) {
  const score = perf.kpis.score;
  const data = useMemo(() => carryToEnd(perf.series.map((p) => ({ label: p.label, value: p.value }))), [perf]);
  const domain = useMemo(() => scoreDomain(data.map((d) => d.value)), [data]);
  return (
    <Panel class="perf-score" title={title} actions={actions}>
      <svg class="perf-defs" width="0" height="0" aria-hidden="true" focusable="false">
        <defs>
          <linearGradient id="perf-fill" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0" class="perf-stop-top" />
            <stop offset="1" class="perf-stop-bottom" />
          </linearGradient>
        </defs>
      </svg>
      <div class="perf-score-head">
        <span class="perf-score-value">{formatScore(score.value)}</span>
        <span class="perf-score-of">/ 100</span>
        <ScoreInfo />
        <Delta value={score.delta} format={formatPoints} />
        <span class="meta">{isNum(score.delta) ? priorText(range) : rangeOf(range).long}</span>
        {perf.scoreWindow ? <span class="meta perf-line-note">Line: trailing {perf.scoreWindow} days</span> : null}
      </div>
      <AreaChart
        class="perf-chart"
        data={data}
        height={220}
        yDomain={domain}
        format={axisScore}
        label={`${title} score, ${rangeOf(range).long.toLowerCase()}`}
        emptyText={emptyText}
        connectNulls
        smooth
        markStart
      />
    </Panel>
  );
}

// OUTPUT · VIDEOS DELIVERED · ON TIME · ACTIVE · REVIEW SPEED. `summary` (optional) adds the blocked count under ACTIVE.
// Videos delivered (DESIGN §7.6) = the Result counts of the batches finished in the period.
export function KpiCards({ kpis, summary, outputLabel = 'Output' }) {
  const review = kpis.reviewSpeed;
  const blocked = summary?.blocked || 0;
  const videos = kpis.videos || { value: 0, delta: null };
  return (
    <div class="perf-kpis">
      <MetricCard size="sm" label={outputLabel} value={kpis.output.value} delta={kpis.output.delta} deltaFormat={formatPercent} />
      <MetricCard size="sm" label="Videos Delivered" value={videos.value} delta={videos.delta} deltaFormat={formatPercent} />
      <MetricCard size="sm" label="On Time" value={formatPct(kpis.onTimeRate.value)} delta={kpis.onTimeRate.delta} deltaFormat={formatPp}
        meta={kpis.onTimeRate.value == null ? 'Nothing finished yet' : ''} />
      <MetricCard size="sm" label="Active" value={kpis.activeWorkload.value}
        meta={blocked ? `${blocked} blocked` : ''} metaTone="danger" />
      <MetricCard size="sm" label="Review Speed"
        value={isNum(review.value) ? <>{one(review.value)}<span class="perf-unit">days</span></> : '—'}
        delta={review.delta} deltaFormat={formatPercent} invert
        meta={isNum(review.value) ? '' : 'No reviews finished'} />
    </div>
  );
}

// ---------- team ----------

const byName = (a, b) => a.name.localeCompare(b.name, 'en', { sensitivity: 'base' });

// Alphabetical rows (never ranked). Unscoped → derive.peopleStats; with a Client/Month scope the same numbers are
// computed for that scope so the list always matches the cards above it.
export function teamRows(state, index, { range, clientId = '', monthNumber = 0, today }) {
  if (!clientId && !monthNumber) return peopleStats(state, index, { range, today });
  return [...state.team].sort(byName).map((person) => {
    const p = computePerformance(state, index, { range, clientId, monthNumber, today, teamMemberId: person.id });
    return {
      id: person.id, person, name: person.name, role: person.role || '',
      completed: p.kpis.output.value, onTimeRate: p.kpis.onTimeRate.value, score: p.kpis.score.value,
      delta: p.kpis.score.delta, active: activeNow(state, index, person.id, { clientId, monthNumber }),
    };
  });
}

export function personLink(id, range) {
  return href(buildPath(`/people/${encodeURIComponent(id)}`, { range: range === DEFAULT_RANGE ? '' : range }));
}

function teamRowLabel(r) {
  const trend = isNum(r.delta) ? `, trend ${formatPoints(r.delta)}` : '';
  return `${r.name}${r.role ? `, ${r.role}` : ''}: ${plural(r.completed || 0, 'batch', 'batches')} completed, ${formatPct(r.onTimeRate)} on time, ${r.active} active${trend}`;
}

function TeamList({ rows, range }) {
  if (!rows.length) {
    return (
      <Panel title="Team">
        <EmptyState compact title="No team members yet." action={<a class="btn btn-secondary btn-sm" href={href('/people')}>Open People</a>} />
      </Panel>
    );
  }
  return (
    <Panel title="Team" meta={`${rows.length}`} flush>
      <div class="perf-team">
        <div class="table-row table-head" aria-hidden="true">
          <span>Member</span>
          <span class="cell-end pt-done">Completed</span>
          <span class="cell-end pt-ontime">On time</span>
          <span class="cell-end pt-trend" title="Score change vs the prior period">Trend</span>
          <span class="cell-end pt-active">Active</span>
        </div>
        {rows.map((r) => (
          <a key={r.id} class="table-row pt-row" href={personLink(r.id, range)} aria-label={teamRowLabel(r)}>
            <span class="pt-member">
              <Avatar name={r.name} size={30} />
              <span class="pt-name">
                <span class="truncate strong">{r.name}</span>
                <span class="truncate meta">{r.role || 'Team member'}</span>
              </span>
            </span>
            <span class="pt-stats">
              <span class="cell-end cell-num pt-done">{plural(r.completed || 0, 'batch', 'batches')}</span>
              <span class="cell-end cell-num pt-ontime">{formatPct(r.onTimeRate)}<span class="pt-unit"> on time</span></span>
              <span class="cell-end cell-num pt-active">{r.active}<span class="pt-unit"> active</span></span>
            </span>
            <span class="cell-end cell-num pt-trend">
              {isNum(r.delta) ? <Delta value={r.delta} format={formatPoints} /> : <span class="muted" aria-label="No trend">—</span>}
            </span>
          </a>
        ))}
      </div>
    </Panel>
  );
}

// ---------- view ----------

// Client ▾ / Month ▾ (tiny, ghost) + range buttons — the header actions of the score card.
// Month ▾ lists every month the scoped client (or any client) runs to — up to 99, never capped at 24.
function ScopeControls({ state, index, today, scope }) {
  const clients = useMemo(() => [...state.clients].sort(byName), [state.clients]);
  const clientOptions = [{ value: '', label: 'All clients' }, ...clients.map((c) => ({ value: c.id, label: c.name, meta: c.status }))];
  const monthOptions = [{ value: 0, label: 'All months' }];
  const scoped = scope.clientId ? clients.filter((c) => c.id === scope.clientId) : clients;
  const last = Math.max(scope.monthNumber, ...scoped.map((c) => monthCountOf(index, c, today)), 1);
  for (let n = 1; n <= last; n += 1) monthOptions.push({ value: n, label: monthLabel(n) });
  return (
    <div class="perf-controls">
      <CompactDropdown size="sm" label="Client:" value={scope.clientId} options={clientOptions}
        valueLabel={scope.clientId ? undefined : 'All'} onChange={(v) => setQuery({ client: v || null })} width={260} />
      <CompactDropdown size="sm" label="Month:" value={scope.monthNumber} options={monthOptions}
        valueLabel={scope.monthNumber ? undefined : 'All'} onChange={(v) => setQuery({ month: v || null })} width={180} />
      <RangeButtons value={scope.range} onChange={(v) => setQuery({ range: v === DEFAULT_RANGE ? null : v })} />
    </div>
  );
}

export function PerformanceView({ route }) {
  const { state, index, today } = useStore();
  const scope = scopeFromQuery(route.query, index);
  const { range, clientId, monthNumber } = scope;
  const perf = useMemo(() => computePerformance(state, index, { range, clientId, monthNumber, today }), [state, index, today, range, clientId, monthNumber]);
  const team = useMemo(() => teamRows(state, index, { range, clientId, monthNumber, today }), [state, index, today, range, clientId, monthNumber]);
  const scoped = !!(clientId || monthNumber);
  const scopeText = [clientId && index.clientsById.get(clientId)?.name, monthNumber && monthLabel(monthNumber)].filter(Boolean).join(' · ');
  if (!state.batches.length) {
    return (
      <Page title="Performance">
        <EmptyState class="perf-first" title="Nothing to measure yet."
          description="Performance appears once your team is working on batches: output, on-time rate and each person’s trend." />
      </Page>
    );
  }
  return (
    <Page title="Performance" subtitle={[formatDateRange(perf.period.start, perf.period.end), scopeText].filter(Boolean).join(' · ')}>
      <div class={cx('perf', scoped && 'is-scoped')}>
        <ScoreCard perf={perf} range={range} actions={<ScopeControls state={state} index={index} today={today} scope={scope} />} />
        <KpiCards kpis={perf.kpis} summary={perf.summary} />
        <TeamList rows={team} range={range} />
      </div>
    </Page>
  );
}
